import * as React from 'react';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { OneTimeOrderStep1 } from './OneTimeOrderStep1';
import { OneTimeOrderStep2 } from './OneTimeOrderStep2';
import { axios } from '../../api';
import { Close } from '../../assets/Close';
import { formatingOptions } from '../../formatingOptions';
import { useGetProducts } from '../../hooks/getProducts';
import { useEstimatedDeliveryDate } from '../../hooks/useShippingRates';
import { Customer, GetProductsRequestType, Option, ProductCodesResponse } from '../../pages/types';
import {
  BOTTOM_TOAST,
  generateErrorMsg,
  MULTIPLE_MEAL_ONE_TIME_PRODUCTS,
  defaultShippingOption,
  ECONOMY_SHIPPING_OPTION,
  formatCountryCode,
  generateFullName,
  getCountryFlag,
  validateOneTimeOrderProducts,
} from '../../utils';
import { LineInfo } from '../LineInfo';

export type OneTimeOrderContextData = {
  customer: Customer;
  dogs: Option[];
  fullProducts: ProductCodesResponse[];
  selectedDog?: Option;
  setSelectedDog: Dispatch<SetStateAction<Option | undefined>>;
  selectedProducts: Option[];
  setSelectedProducts: Dispatch<SetStateAction<Option[]>>;
  isEverythingSelected: boolean;
  isLoading: boolean;
  navigateBetweenSteps: () => void;
  goBack: () => void;
  paymentMethod: string;
  selectedShippingMethod: Option;
  setSelectedShippingMethod: Dispatch<SetStateAction<Option>>;
  shippingOptions: Option[];
  isLoadingShippingRates: boolean;
  onConfirm: () => void;
};

export const OneTimeOrderContext = React.createContext<OneTimeOrderContextData>(
  {} as OneTimeOrderContextData
);

type OneTimeOrderModalProps = {
  customer: Customer;
  onConfirm: () => void;
  onCancel: () => void;
};

export const OneTimeOrderModal = ({ customer, onConfirm, onCancel }: OneTimeOrderModalProps) => {
  const { pets, addresses } = customer;

  const [selectedDog, setSelectedDog] = useState<Option>();
  const [selectedProducts, setSelectedProducts] = useState<Option[]>([]);
  const [shippingAddress, setShippingAddress] = useState<string>();
  const [selectedShippingMethod, setSelectedShippingMethod] =
    useState<Option>(ECONOMY_SHIPPING_OPTION);
  const productsHasFrozen = useMemo(() => {
    return selectedProducts.some((selectedProduct) => {
      const product = selectedProduct as unknown as ProductCodesResponse;
      return product.is_frozen;
    });
  }, [selectedProducts]);

  const productsHasAmbient = useMemo(() => {
    return selectedProducts.some((selectedProduct) => {
      const product = selectedProduct as unknown as ProductCodesResponse;
      return !product.is_frozen;
    });
  }, [selectedProducts]);

  const { shippingOptions, isLoadingShippingRates } = useEstimatedDeliveryDate(
    customer.id,
    productsHasFrozen,
    productsHasAmbient
  );

  const [paymentMethod, setPaymentMethod] = useState<string>('--');
  const { products, isLoading } = useGetProducts(
    GetProductsRequestType.ONE_TIME_ORDER_PRODUCTS,
    customer.default_country
  );

  const [currentStep, setCurrentStep] = useState(1);

  const dogs = formatingOptions(pets, { value: 'id', label: 'name' });

  const getPaymentMethod = () => {
    axios
      .get(`payment_methods/?account_id=${customer.id}`)
      .then((response) => {
        const defaultPaymentMethod = response.data.find((card: any) => card.is_default === true);
        if (defaultPaymentMethod?.card) {
          setPaymentMethod(
            `${defaultPaymentMethod.card.brand} x${defaultPaymentMethod.card.last4}`
          );
        }
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  useEffect(() => {
    getPaymentMethod();
    const primaryShippingAddress = addresses?.find(
      (address) => address.type === 'SHIPPING' && address.is_primary
    );

    if (primaryShippingAddress) {
      const { address1, address2, city, state, zip, country } = primaryShippingAddress;
      const formattedCountry = formatCountryCode(country);
      setShippingAddress(
        [address1, address2, city, state, zip, formattedCountry].filter(Boolean).join(', ')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default shipping method to ECONOMY when shipping options are loaded
  useEffect(() => {
    if (shippingOptions.length > 0) {
      const defaultCustomerShippingOption = defaultShippingOption(shippingOptions, customer);
      if (defaultCustomerShippingOption) {
        setSelectedShippingMethod(defaultCustomerShippingOption);
      }
    }
  }, [customer, customer.default_shipping_method_id, shippingOptions]);

  const isEverythingSelected = useMemo(
    () => !!selectedDog?.label && selectedProducts.length > 0,
    [selectedDog?.label, selectedProducts.length]
  );

  const navigateBetweenSteps = () => {
    try {
      validateOneTimeOrderProducts(
        products.filter((product) =>
          selectedProducts.some((selectedCode) => selectedCode.label === product.code)
        )
      );
      setCurrentStep(isEverythingSelected ? 2 : 1);
    } catch {
      toast.error(MULTIPLE_MEAL_ONE_TIME_PRODUCTS, BOTTOM_TOAST);
    }
  };

  const goBack = () => {
    setCurrentStep(1);
  };

  const context = {
    customer,
    dogs,
    fullProducts: products,
    selectedDog,
    setSelectedDog,
    selectedProducts,
    setSelectedProducts,
    isEverythingSelected,
    isLoading,
    navigateBetweenSteps,
    goBack,
    paymentMethod,
    selectedShippingMethod,
    setSelectedShippingMethod,
    shippingOptions,
    isLoadingShippingRates,
    onConfirm,
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal justify-self-center max-w-[80vw]">
      <div className="bg-white rounded-lg shadow-md p-4 min-w-[700px] relative border">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-left mb-2">Create One-Time Order</h1>
        <LineInfo
          title="Customer"
          value={`${getCountryFlag(customer.default_country)} ${generateFullName(
            customer.first_name,
            customer.last_name
          )} (${customer.email})`}
        />
        <LineInfo title="Primary Shipping Address" value={shippingAddress} />
        <LineInfo title="Default Payment Method" value={paymentMethod} />
        <hr />
        <OneTimeOrderContext.Provider value={context}>
          {currentStep === 1 ? <OneTimeOrderStep1 /> : <OneTimeOrderStep2 />}
        </OneTimeOrderContext.Provider>
      </div>
    </div>
  );
};
